import { Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { isUndefined } from 'lodash';
import { useEffect } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form';
import {
  DispositiveProvisionDispositionKind,
  DispositiveProvisionTemplateKind,
  DispositiveProvisionTransferTaxKind,
} from '@/types/schema';

import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import {
  DispositiveProvisionsFormPaths,
  DispositiveProvisionsFormRecipient,
  DispositiveProvisionsTemplateSlotKind,
} from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { SURVIVING_SPOUSE_SENTINEL } from '../../DispositiveProvisionsForm/hooks/useRecipientOptions';
import { DispositiveProvisionsTemplateEditorTabHeader } from '../DispositiveProvisionsTemplateSplitScreenModal.components';
import { BLANK_DISCLAIMER_TRUST } from '../DispositiveProvisionsTemplateSplitScreenModal.constants';
import {
  DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE,
  DispositiveProvisionsTemplateShape,
} from '../DispositiveProvisionsTemplateSplitScreenModal.types';
import { TemplateRecipientSlot } from './DispositiveProvisionsTemplateSplitScreenModal.components';

export function DispositiveProvisionsTemplateOutrightToSurvivingSpousePreset() {
  const { control, setValue } =
    useFormContext<DispositiveProvisionsTemplateShape>();
  const {
    append,
    replace,
    fields: recipientFields,
  } = useFieldArray({
    control,
    name: `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients` as const satisfies DispositiveProvisionsFormPaths,
    keyName: '_id',
  });
  const [recipients, includeDisclaimerTrust] = useWatch({
    control,
    name: [
      `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients`,
      `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.includeDisclaimerTrust`,
    ],
  });

  const includeDisclaimerTrustPrevious = usePrevious(includeDisclaimerTrust);

  useEffect(() => {
    if (isUndefined(includeDisclaimerTrustPrevious)) return;
    if (includeDisclaimerTrustPrevious === includeDisclaimerTrust) return;

    const hasDisclaimer = recipients.some(
      (recipient) =>
        recipient._templateSlotKind ===
        DispositiveProvisionsTemplateSlotKind.OutrightDisclaimer
    );
    if (includeDisclaimerTrust === hasDisclaimer) return;

    const updatedRecipients: DispositiveProvisionsFormRecipient[] =
      recipients.map((recipient): DispositiveProvisionsFormRecipient => {
        if (
          recipient._templateSlotKind !==
          DispositiveProvisionsTemplateSlotKind.OutrightSurvivingSpouse
        ) {
          return recipient;
        }
        return includeDisclaimerTrust
          ? {
              ...recipient,
              dispositionKind_ButtonGroup: 'other',
              dispositionKind_Select:
                DispositiveProvisionDispositionKind.AnythingLeftOver,
              dispositionPercentage: null,
            }
          : {
              ...recipient,
              dispositionKind_ButtonGroup:
                DispositiveProvisionDispositionKind.Percentage,
              dispositionKind_Select: '',
              dispositionPercentage: new Decimal(100),
            };
      });
    replace(
      includeDisclaimerTrust
        ? [BLANK_DISCLAIMER_TRUST, ...updatedRecipients]
        : updatedRecipients.filter(
            (recipient) =>
              recipient._templateSlotKind !==
              DispositiveProvisionsTemplateSlotKind.OutrightDisclaimer
          )
    );
  }, [
    includeDisclaimerTrust,
    includeDisclaimerTrustPrevious,
    recipients,
    replace,
  ]);

  const handleRemoveRecipient = (index: number) => {
    const recipient = recipients[index];
    if (
      recipient?._templateSlotKind ===
      DispositiveProvisionsTemplateSlotKind.OutrightDisclaimer
    ) {
      const nextRecipients: DispositiveProvisionsFormRecipient[] =
        recipients.map(
          (currentRecipient): DispositiveProvisionsFormRecipient => {
            if (
              currentRecipient._templateSlotKind !==
              DispositiveProvisionsTemplateSlotKind.OutrightSurvivingSpouse
            ) {
              return currentRecipient;
            }
            return {
              ...currentRecipient,
              dispositionKind_ButtonGroup:
                DispositiveProvisionDispositionKind.Percentage,
              dispositionKind_Select: '',
              dispositionPercentage: new Decimal(100),
            };
          }
        );
      replace(
        nextRecipients.filter((_, recipientIndex) => recipientIndex !== index)
      );
      setValue(
        `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.includeDisclaimerTrust`,
        false
      );
      return;
    }
    replace(recipients.filter((_, recipientIndex) => recipientIndex !== index));
  };

  return (
    <Stack direction="column" spacing={2}>
      <DispositiveProvisionsTemplateEditorTabHeader
        templateType={
          DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse
        }
        recipients={recipientFields}
        onAddTemplateRecipient={append}
      />
      <Card variant="filled-callout" sx={{ padding: 3 }}>
        <FormAwareSwitch<DispositiveProvisionsTemplateShape>
          control={control}
          label="Include disclaimer trust"
          fieldName={`${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.includeDisclaimerTrust`}
          labelPosition="right"
        />
      </Card>
      {recipientFields.map((recipient, index) => (
        <TemplateRecipientSlot
          key={recipient._id}
          index={index}
          slotKind={recipient._templateSlotKind}
          onRemove={() => handleRemoveRecipient(index)}
        />
      ))}
    </Stack>
  );
}

export const BLANK_OUTRIGHT_TO_SURVIVING_SPOUSE: DispositiveProvisionsFormRecipient =
  {
    _templateSlotKind:
      DispositiveProvisionsTemplateSlotKind.OutrightSurvivingSpouse,
    _dispositiveProvisionId: null,
    recipientId: SURVIVING_SPOUSE_SENTINEL,
    recipientKind: null,
    notes: '',
    dispositionAmount: null,
    dispositionKind_ButtonGroup: DispositiveProvisionDispositionKind.Percentage,
    dispositionKind_Select: '',
    dispositionPercentage: new Decimal(100),
    transferTaxKind: DispositiveProvisionTransferTaxKind.SpouseMaritalExclusion,
    assetGroupIds: [],
    assetStaticIds: [],
    insurancePolicyIds: [],
    ownershipStakeIds: [],
    dispositionAssetPercentage: null,
  };
